// A formula read into a tree, as Google Sheets reads one: numbers, "text", TRUE and FALSE, references (A1, $A$1, A1:B10,
// A:A, 2:2, A2:A, 'Tab name'!A1), operators (+ - * / ^ & = <> < > <= >= and %), {1,2;3,4}, and functions. Formulas are
// data, never code: they are read here and worked out by shared/sheet/engine.ts, never run.
//
// The tokens keep where they sit in the formula, so a formula can be written again with its references moved
// (shared/sheet/shift.ts) and every other character as the user typed it.

import { colIndex, colName } from "./addr";

export type ErrCode = "#REF!" | "#DIV/0!" | "#NAME?" | "#VALUE!" | "#N/A" | "#NUM!" | "#NULL!" | "#CYCLE!" | "#ERROR!";
export const ERR_CODES: ErrCode[] = ["#REF!", "#DIV/0!", "#NAME?", "#VALUE!", "#N/A", "#NUM!", "#NULL!", "#CYCLE!", "#ERROR!"];

/** One end of a reference: a column, a row, or both, each fixed with $ or not. */
export interface RefEnd {
  c: number | null;
  r: number | null;
  ac: boolean;
  ar: boolean;
}

/** A reference as written: the tab it names (null: the formula's own), and one or two ends. */
export interface Ref {
  sheet: string | null;
  a: RefEnd;
  b: RefEnd | null;
  /** a reference that pointed at cells since deleted: it stays #REF! */
  dead?: boolean;
}

export type Ast =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "bool"; v: boolean }
  | { t: "err"; v: ErrCode }
  | { t: "ref"; ref: Ref }
  | { t: "fn"; name: string; args: (Ast | null)[] }
  | { t: "un"; op: "-" | "+"; a: Ast }
  | { t: "pct"; a: Ast }
  | { t: "bin"; op: string; a: Ast; b: Ast }
  | { t: "arr"; rows: Ast[][] }
  | { t: "name"; name: string };

export type Tok =
  | { k: "num"; v: number; pos: number; end: number }
  | { k: "str"; v: string; pos: number; end: number }
  | { k: "err"; v: ErrCode; pos: number; end: number }
  | { k: "ref"; ref: Ref; pos: number; end: number }
  | { k: "fn"; v: string; pos: number; end: number }
  | { k: "name"; v: string; pos: number; end: number }
  | { k: "op"; v: string; pos: number; end: number }
  | { k: "("; pos: number; end: number }
  | { k: ")"; pos: number; end: number }
  | { k: ","; pos: number; end: number }
  | { k: ";"; pos: number; end: number }
  | { k: "{"; pos: number; end: number }
  | { k: "}"; pos: number; end: number }
  | { k: "ws"; pos: number; end: number };

export class ParseError extends Error {}

const SHEET = /(?:'((?:[^']|'')+)'|([A-Za-z_][A-Za-z0-9_.]*))!/y;
const CELL = /(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})/y;
const COL = /(\$?)([A-Za-z]{1,3})/y;
const ROW = /(\$?)([0-9]{1,7})/y;
const NUM = /(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?/y;
const IDENT = /[A-Za-z_][A-Za-z0-9_.]*/y;
const AFTER = /[A-Za-z0-9_.(!]/;

function at(re: RegExp, s: string, i: number): RegExpExecArray | null {
  re.lastIndex = i;
  return re.exec(s);
}

function cellEnd(m: RegExpExecArray): RefEnd {
  const c = colIndex(m[2]);
  const r = Number(m[4]) - 1;
  return { c, r, ac: m[1] === "$", ar: m[3] === "$" };
}

/** A reference starting at i (after any tab name), with where it ends; null when none starts there. */
function refAt(s: string, i: number): { a: RefEnd; b: RefEnd | null; end: number } | null {
  const free = (j: number) => j >= s.length || !AFTER.test(s[j]);
  const cell = at(CELL, s, i);
  if (cell) {
    const a = cellEnd(cell);
    let j = i + cell[0].length;
    if (s[j] === ":") {
      const c2 = at(CELL, s, j + 1);
      if (c2 && free(j + 1 + c2[0].length)) return { a, b: cellEnd(c2), end: j + 1 + c2[0].length };
      const col = at(COL, s, j + 1);
      if (col && free(j + 1 + col[0].length)) return { a, b: { c: colIndex(col[2]), r: null, ac: col[1] === "$", ar: false }, end: j + 1 + col[0].length };
    }
    if (free(j)) return { a, b: null, end: j };
    return null;
  }
  const col = at(COL, s, i);
  if (col && s[i + col[0].length] === ":") {
    const j = i + col[0].length + 1;
    const col2 = at(COL, s, j);
    if (col2 && free(j + col2[0].length) && !at(CELL, s, j)) return { a: { c: colIndex(col[2]), r: null, ac: col[1] === "$", ar: false }, b: { c: colIndex(col2[2]), r: null, ac: col2[1] === "$", ar: false }, end: j + col2[0].length };
  }
  const row = at(ROW, s, i);
  if (row && s[i + row[0].length] === ":") {
    const j = i + row[0].length + 1;
    const row2 = at(ROW, s, j);
    if (row2 && free(j + row2[0].length)) return { a: { c: null, r: Number(row[2]) - 1, ac: false, ar: row[1] === "$" }, b: { c: null, r: Number(row2[2]) - 1, ac: false, ar: row2[1] === "$" }, end: j + row2[0].length };
  }
  return null;
}

/** A formula's text (after its =) as tokens, spaces kept, each with where it sits. */
export function tokenize(s: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      let j = i;
      while (j < s.length && /\s/.test(s[j])) j++;
      out.push({ k: "ws", pos: i, end: j });
      i = j;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      let v = "";
      for (;;) {
        if (j >= s.length) throw new ParseError("A text in quotes is not closed.");
        if (s[j] === '"') {
          if (s[j + 1] === '"') {
            v += '"';
            j += 2;
            continue;
          }
          j++;
          break;
        }
        v += s[j++];
      }
      out.push({ k: "str", v, pos: i, end: j });
      i = j;
      continue;
    }
    if (ch === "#") {
      const code = ERR_CODES.find((c) => s.slice(i, i + c.length).toUpperCase() === c);
      if (!code) throw new ParseError("Unknown error value.");
      out.push({ k: "err", v: code, pos: i, end: i + code.length });
      i += code.length;
      continue;
    }
    // a reference, with the tab it names first
    const sheet = at(SHEET, s, i);
    if (sheet) {
      const ref = refAt(s, i + sheet[0].length);
      if (ref) {
        const name = sheet[1] !== undefined ? sheet[1].replace(/''/g, "'") : sheet[2];
        out.push({ k: "ref", ref: { sheet: name, a: ref.a, b: ref.b }, pos: i, end: ref.end });
        i = ref.end;
        continue;
      }
      // a tab's name followed by #REF!: a reference to cells since deleted
      if (s.slice(i + sheet[0].length, i + sheet[0].length + 5).toUpperCase() === "#REF!") {
        const name = sheet[1] !== undefined ? sheet[1].replace(/''/g, "'") : sheet[2];
        out.push({ k: "ref", ref: { sheet: name, a: { c: 0, r: 0, ac: false, ar: false }, b: null, dead: true }, pos: i, end: i + sheet[0].length + 5 });
        i += sheet[0].length + 5;
        continue;
      }
    }
    const prev = out.filter((t) => t.k !== "ws").at(-1);
    // after a value a reference cannot start (2A1 is no formula), but a row range can follow an operator
    if (!prev || (prev.k !== "num" && prev.k !== "ref" && prev.k !== ")" && prev.k !== "str")) {
      const ref = refAt(s, i);
      if (ref) {
        out.push({ k: "ref", ref: { sheet: null, a: ref.a, b: ref.b }, pos: i, end: ref.end });
        i = ref.end;
        continue;
      }
    }
    const num = at(NUM, s, i);
    if (num) {
      out.push({ k: "num", v: Number(num[0]), pos: i, end: i + num[0].length });
      i += num[0].length;
      continue;
    }
    const id = at(IDENT, s, i);
    if (id) {
      let j = i + id[0].length;
      let k = j;
      while (k < s.length && s[k] === " ") k++;
      if (s[k] === "(") out.push({ k: "fn", v: id[0].toUpperCase(), pos: i, end: j });
      else if (s[j] === "[") {
        // a table's column, Orders[Total] (Features 5): one name, the words in brackets its header's
        const close = s.indexOf("]", j + 1);
        if (close < 0) throw new ParseError(`${id[0]}[ is not closed.`);
        out.push({ k: "name", v: s.slice(i, close + 1), pos: i, end: close + 1 });
        j = close + 1;
      } else out.push({ k: "name", v: id[0], pos: i, end: j });
      i = j;
      continue;
    }
    const two = s.slice(i, i + 2);
    if (two === "<=" || two === ">=" || two === "<>") {
      out.push({ k: "op", v: two, pos: i, end: i + 2 });
      i += 2;
      continue;
    }
    if ("+-*/^&=<>%".includes(ch)) {
      out.push({ k: "op", v: ch, pos: i, end: i + 1 });
      i++;
      continue;
    }
    if (ch === "(" || ch === ")" || ch === "," || ch === ";" || ch === "{" || ch === "}") {
      out.push({ k: ch, pos: i, end: i + 1 } as Tok);
      i++;
      continue;
    }
    throw new ParseError(`Unexpected "${ch}".`);
  }
  return out;
}

const BINARY: Record<string, number> = { "=": 1, "<>": 1, "<": 1, ">": 1, "<=": 1, ">=": 1, "&": 2, "+": 3, "-": 3, "*": 4, "/": 4, "^": 5 };

/** A formula's text (after its =) as a tree; ParseError for one that cannot be read. */
export function parse(src: string): Ast {
  const toks = tokenize(src).filter((t) => t.k !== "ws");
  let i = 0;
  let depth = 0;
  const peek = () => toks[i];
  const next = () => toks[i++];
  const expect = (k: Tok["k"]) => {
    const t = next();
    if (!t || t.k !== k) throw new ParseError(`Expected "${k}".`);
    return t;
  };
  const expr = (min: number): Ast => {
    if (++depth > 200) throw new ParseError("That formula is nested too deep.");
    let left = prefix();
    for (;;) {
      const t = peek();
      if (!t) break;
      if (t.k === "op" && t.v === "%") {
        next();
        left = { t: "pct", a: left };
        continue;
      }
      if (t.k !== "op" || !(t.v in BINARY)) break;
      const bp = BINARY[t.v];
      if (bp < min) break;
      next();
      // left to right at every level, ^ included, as Sheets and Excel read 2^3^2
      const right = expr(bp + 1);
      left = { t: "bin", op: t.v, a: left, b: right };
    }
    depth--;
    return left;
  };
  const prefix = (): Ast => {
    const t = next();
    if (!t) throw new ParseError("The formula ends too soon.");
    switch (t.k) {
      case "num":
        return { t: "num", v: t.v };
      case "str":
        return { t: "str", v: t.v };
      case "err":
        return { t: "err", v: t.v };
      case "ref":
        return { t: "ref", ref: t.ref };
      case "name": {
        const u = t.v.toUpperCase();
        if (u === "TRUE" || u === "FALSE") return { t: "bool", v: u === "TRUE" };
        return { t: "name", name: t.v };
      }
      case "op":
        // a sign binds tighter than ^, as in Sheets: -2^2 is 4
        if (t.v === "-" || t.v === "+") return { t: "un", op: t.v, a: expr(6) };
        throw new ParseError(`Unexpected "${t.v}".`);
      case "(": {
        const e = expr(0);
        expect(")");
        return e;
      }
      case "{": {
        const rows: Ast[][] = [[]];
        for (;;) {
          rows[rows.length - 1].push(expr(0));
          const sep = next();
          if (!sep) throw new ParseError("An array is not closed.");
          if (sep.k === "}") break;
          if (sep.k === ";") rows.push([]);
          else if (sep.k !== ",") throw new ParseError("Unexpected token in an array.");
        }
        if (rows.some((r) => r.length !== rows[0].length)) throw new ParseError("Every row of an array has as many values.");
        return { t: "arr", rows };
      }
      case "fn": {
        expect("(");
        const args: (Ast | null)[] = [];
        if (peek()?.k === ")") {
          next();
          return { t: "fn", name: t.v, args };
        }
        for (;;) {
          const p = peek();
          if (p && (p.k === "," || p.k === ")")) args.push(null);
          else args.push(expr(0));
          const sep = next();
          if (!sep) throw new ParseError(`${t.v}( is not closed.`);
          if (sep.k === ")") break;
          if (sep.k !== ",") throw new ParseError("Expected , or ).");
        }
        if (args.length > 255) throw new ParseError("Too many arguments.");
        return { t: "fn", name: t.v, args };
      }
      default:
        throw new ParseError("Unexpected token.");
    }
  };
  if (!toks.length) throw new ParseError("The formula is empty.");
  const ast = expr(0);
  if (i < toks.length) throw new ParseError("Unexpected token after the formula.");
  return ast;
}

/** A reference's end as written: $A$1, A1, $A, 3. */
export function endText(e: RefEnd): string {
  const col = e.c === null ? "" : (e.ac ? "$" : "") + colName(e.c);
  const row = e.r === null ? "" : (e.ar ? "$" : "") + (e.r + 1);
  return col + row;
}

/** A tab's name as a formula writes it: in quotes when it holds more than letters, digits and _. */
export function sheetText(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !/^[A-Za-z]{1,3}[0-9]+$/.test(name) ? name : `'${name.replace(/'/g, "''")}'`;
}

/** A reference as a formula writes it. */
export function refText(ref: Ref): string {
  const pre = ref.sheet !== null ? `${sheetText(ref.sheet)}!` : "";
  if (ref.dead) return `${pre}#REF!`;
  return pre + endText(ref.a) + (ref.b ? `:${endText(ref.b)}` : "");
}
